import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { ReviewFields } from "@/components/reviews/ReviewFields";
import { PhotoUploader } from "@/components/reviews/PhotoUploader";
import { useSubmitReview } from "@/hooks/useReviews";
import { usePhotoUploads } from "@/hooks/usePhotoUploads";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { validateReview } from "@/lib/validation";

const EMPTY = { rating: 0, name: "", email: "", title: "", body: "" };

export function ReviewForm({ product, open, onOpenChange }) {
  const { user } = useAuth();
  const submit = useSubmitReview(product.handle);
  const { photos, addFiles, removePhoto, reset: resetPhotos, uploading, photoIds } = usePhotoUploads();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, name: user?.name || "", email: user?.email || "" });
      setErrors({});
      resetPhotos();
    }
  }, [open, user, resetPhotos]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const nextErrors = validateReview(form);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length || uploading) return;
    try {
      await submit.mutateAsync({
        name: form.name.trim(),
        email: form.email.trim(),
        rating: form.rating,
        title: form.title.trim() || null,
        body: form.body.trim(),
        photo_ids: photoIds,
      });
      toast.success("Thank you — your review is now live.");
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit your review");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto border-border bg-ivory p-0 sm:rounded-none" data-testid="review-form-dialog">
        <form onSubmit={handleSubmit} noValidate className="p-6 sm:p-8">
          <p className="text-luxe text-[10px] text-gold">Share your experience</p>
          <DialogTitle className="mt-1 font-serif text-3xl font-light text-ink">Write a Review</DialogTitle>
          <DialogDescription className="mt-1 text-sm text-muted-foreground">{product.title}</DialogDescription>

          <ReviewFields
            form={form}
            errors={errors}
            onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
            onRating={(r) => setForm((f) => ({ ...f, rating: r }))}
          />

          <div className="mt-5">
            <PhotoUploader photos={photos} onAdd={addFiles} onRemove={removePhoto} />
          </div>

          <button
            type="submit"
            disabled={submit.isPending || uploading}
            data-testid="review-form-submit"
            className="mt-7 flex w-full items-center justify-center gap-2 bg-ink py-4 text-[11px] font-medium uppercase tracking-[0.18em] text-ivory transition-colors hover:bg-gold disabled:cursor-not-allowed disabled:bg-muted-foreground/60"
          >
            {submit.isPending && <Loader2 size={15} className="animate-spin" />}
            {uploading ? "Uploading photos…" : "Submit Review"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
